import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { auth as authAPI } from '../services/api';
import Avatar from '../components/Avatar';
import '../styles/Profile.css';

const PublicProfile = () => {
  const { userId } = useParams();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchProfileData();
  }, [userId]);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await authAPI.getPublicProfile(userId);
      setProfileData(response.data);

    } catch (err) {
      setError(err.response?.data?.message || 'Nem sikerült betölteni a profilt');
      console.error('Error fetching public profile:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ padding: '3rem 0', textAlign: 'center' }}>
        <p>Betöltés...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container" style={{ padding: '3rem 0', textAlign: 'center' }}>
        <p style={{ color: 'var(--error-color)' }}>{error}</p>
        <Link to="/recipes" className="btn btn-primary" style={{ marginTop: '1rem' }}>
          Vissza a receptekhez
        </Link>
      </div>
    );
  }

  if (!profileData) {
    return null;
  }

  // If profile is private, show limited view
  if (!profileData.isPublic) {
    return (
      <div className="profile-container">
        <div className="profile-header">
          <div className="container">
            <div className="profile-header-content" style={{ alignItems: 'center', textAlign: 'center', flexDirection: 'column' }}>
              <Avatar
                name={profileData.name}
                imageUrl={profileData.profileImage}
                size="xlarge"
              />
              <div className="profile-header-info" style={{ textAlign: 'center' }}>
                <div className="profile-name-row" style={{ justifyContent: 'center' }}>
                  <h1 className="profile-name">
                    {profileData.nickname || profileData.name}
                  </h1>
                  <span className="profile-badge badge-private">
                    🔒 Privát profil
                  </span>
                </div>
                <p style={{ color: 'var(--text-muted)', marginTop: '1rem' }}>
                  Ez a felhasználó privátra állította a profilját
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Format date
  const memberSince = new Date(profileData.createdAt).toLocaleDateString('hu-HU', {
    year: 'numeric',
    month: 'long'
  });

  return (
    <div className="profile-container">
      {/* Profile Header */}
      <div className="profile-header">
        <div className="container">
          <div className="profile-header-content">
            <Avatar
              name={profileData.name}
              imageUrl={profileData.profileImage}
              size="xlarge"
            />
            <div className="profile-header-info">
              <div className="profile-name-row">
                <h1 className="profile-name">
                  {profileData.nickname || profileData.name}
                  {profileData.nickname && (
                    <span className="profile-fullname">({profileData.name})</span>
                  )}
                </h1>
                <span className="profile-badge badge-public">
                  🌐 Publikus profil
                </span>
              </div>

              {profileData.bio && (
                <p className="profile-bio">{profileData.bio}</p>
              )}

              <div className="profile-meta">
                {profileData.city && (
                  <span className="profile-meta-item">
                    📍 {profileData.city}
                  </span>
                )}
                <span className="profile-meta-item">
                  📅 Tag {memberSince} óta
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="profile-stats">
        <div className="container">
          <div className="profile-stats-grid">
            <div className="profile-stat-card">
              <div className="profile-stat-icon">📖</div>
              <div className="profile-stat-value">{profileData.stats.recipesCount}</div>
              <div className="profile-stat-label">Receptek</div>
            </div>

            <div className="profile-stat-card">
              <div className="profile-stat-icon">❤️</div>
              <div className="profile-stat-value">{profileData.stats.totalLikes}</div>
              <div className="profile-stat-label">Like-ok</div>
            </div>

            <div className="profile-stat-card">
              <div className="profile-stat-icon">⭐</div>
              <div className="profile-stat-value">{profileData.stats.favoritesCount}</div>
              <div className="profile-stat-label">Kedvencek</div>
            </div>

            <div className="profile-stat-card">
              <div className="profile-stat-icon">👁️</div>
              <div className="profile-stat-value">{profileData.stats.totalViews}</div>
              <div className="profile-stat-label">Megtekintések</div>
            </div>
          </div>
        </div>
      </div>

      {/* User Recipes */}
      <div className="profile-recipes">
        <div className="container">
          <div className="profile-recipes-header">
            <h2>{profileData.nickname || profileData.name} receptjei</h2>
          </div>

          {profileData.recipes.length === 0 ? (
            <div className="profile-empty">
              <p>Ennek a felhasználónak még nincs receptje.</p>
            </div>
          ) : (
            <div className="profile-recipes-grid">
              {profileData.recipes.map(recipe => (
                <Link
                  key={recipe._id}
                  to={`/recipes/${recipe._id}`}
                  className="profile-recipe-card"
                >
                  {recipe.coverImage && (
                    <div
                      className="profile-recipe-image"
                      style={{
                        backgroundImage: `url(${recipe.coverImage.medium || recipe.coverImage})`
                      }}
                    />
                  )}
                  <div className="profile-recipe-content">
                    <h3 className="profile-recipe-title">{recipe.title}</h3>
                    <p className="profile-recipe-category">{recipe.category}</p>
                    <div className="profile-recipe-stats">
                      <span>❤️ {recipe.likes?.length || 0}</span>
                      <span>👁️ {recipe.viewCount || 0}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicProfile;
